import React, { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { FaStar } from 'react-icons/fa';
import { motion } from 'framer-motion';
import { trackEvent } from "../../utils/analytics";

const REDIRECT_DELAY = 6000;

const thankYouMessages = {
  en: (name) => name ? `Your tip has been successfully sent to ${name}.` : "Your tip has been successfully sent.",
  fr: (name) => name ? `Votre pourboire a bien été envoyé à ${name}.` : "Votre pourboire a bien été envoyé.",
  es: (name) => name ? `Tu propina ha sido enviada a ${name}.` : "Tu propina ha sido enviada.",
  de: (name) => name ? `Ihr Trinkgeld wurde erfolgreich an ${name} gesendet.` : "Ihr Trinkgeld wurde erfolgreich gesendet.",
  it: (name) => name ? `La mancia è stata inviata a ${name}.` : "La mancia è stata inviata.",
  pt: (name) => name ? `Sua gorjeta foi enviada para ${name}.` : "Sua gorjeta foi enviada.",
  nl: (name) => name ? `Je fooi is verzonden naar ${name}.` : "Je fooi is succesvol verzonden.",
  ru: (name) => name ? `Ваши чаевые отправлены ${name}.` : "Ваши чаевые успешно отправлены.",
  zh: (name) => name ? `您的小费已成功发送给${name}。` : "您的小费已成功发送。",
  ja: (name) => name ? `${name}へのチップが正常に送られました。` : "チップが正常に送られました。",
  ko: (name) => name ? `${name}에게 팁이 성공적으로 전송되었습니다.` : "팁이 성공적으로 전송되었습니다.",
  ar: (name) => name ? `تم إرسال إكراميتك بنجاح إلى ${name}.` : "تم إرسال إكراميتك بنجاح.",
};

const SuccessPage = () => {
  const { t, i18n } = useTranslation();
  const [thankYouMessage, setThankYouMessage] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [linkAvis, setLinkAvis] = useState("");
  const [teamId, setTeamId] = useState("");
  const [progress, setProgress] = useState(100);
  const intervalRef = useRef(null);
  const timerRef = useRef(null);

  useEffect(() => {
    const browserLang = (navigator.language || "en").split("-")[0];
    const storedTeamId = localStorage.getItem("LastTeamId");
    setTeamId(storedTeamId);

    const storedUser = localStorage.getItem("lastTipUser");
    if (storedUser) {
      try {
        const { prenom, nom } = JSON.parse(storedUser);
        setDisplayName(prenom === "Faire dons a toute l'equipe" ? nom : prenom);
      } catch (_) {}
    }

    const storedLink = localStorage.getItem("LinkAvis");
    if (storedLink) setLinkAvis(storedLink);

    // Message construit après avoir récupéré le nom
    const storedUser2 = localStorage.getItem("lastTipUser");
    let resolvedName = "";
    if (storedUser2) {
      try {
        const { prenom, nom } = JSON.parse(storedUser2);
        resolvedName = prenom === "Faire dons a toute l'equipe" ? nom : prenom;
      } catch (_) {}
    }
    const msgFn = thankYouMessages[browserLang] || thankYouMessages["en"];
    setThankYouMessage(msgFn(resolvedName));
    i18n.changeLanguage(browserLang);

    const fallback = storedTeamId ? `/team/${storedTeamId}` : "https://thetipsyou.fr";
    const destination = storedLink && storedLink.trim() !== "" ? storedLink : fallback;

    const start = Date.now();
    intervalRef.current = setInterval(() => {
      const elapsed = Date.now() - start;
      setProgress(Math.max(0, 100 - (elapsed / REDIRECT_DELAY) * 100));
    }, 50);

    timerRef.current = setTimeout(() => {
      clearInterval(intervalRef.current);
      window.location.href = destination;
    }, REDIRECT_DELAY);

    return () => {
      clearInterval(intervalRef.current);
      clearTimeout(timerRef.current);
    };
  }, [i18n]);

  const handleReviewClick = () => {
    trackEvent('click_leave_review_fromSuccess', 'avis', `Avis équipe ${teamId}`);
    clearInterval(intervalRef.current);
    clearTimeout(timerRef.current);
    window.location.href = linkAvis;
  };

  return (
    <div className="min-h-screen bg-modern flex flex-col items-center justify-center px-6 py-12">
      <motion.div
        initial={{ opacity: 0, scale: 0.85 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.4, ease: "easeOut" }}
        className="w-full max-w-sm flex flex-col items-center text-center"
      >
        {/* Icône succès */}
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.15, type: "spring", stiffness: 220, damping: 14 }}
          className="w-20 h-20 md:w-24 md:h-24 bg-black rounded-full flex items-center justify-center mb-6 shadow-xl"
        >
          <svg className="w-10 h-10 md:w-12 md:h-12 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </motion.div>

        {/* Titre */}
        <motion.h1
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3 }}
          className="text-3xl md:text-4xl font-bold text-black mb-2"
        >
          {t("successTitle", "Merci !")}
        </motion.h1>

        {/* Message */}
        <motion.p
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-gray-500 text-sm md:text-base mb-1 px-2"
        >
          {thankYouMessage}
        </motion.p>

        <div className="mb-8" />

        {/* Bouton avis */}
        {linkAvis && (
          <motion.button
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.55 }}
            onClick={handleReviewClick}
            className="w-full flex items-center justify-center gap-3 bg-black text-white font-bold py-4 px-6 rounded-2xl shadow-lg hover:bg-gray-800 active:scale-[0.98] transition mb-8 text-base md:text-lg"
          >
            <FaStar size={18} />
            {t('leaveReviewButton', 'Laisser un avis')}
          </motion.button>
        )}

        {/* Barre countdown */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.65 }}
          className="w-full"
        >
          <div className="w-full bg-gray-200 rounded-full h-1 overflow-hidden">
            <div
              className="bg-black h-1 rounded-full"
              style={{ width: `${progress}%`, transition: 'width 50ms linear' }}
            />
          </div>
          <p className="text-gray-400 text-xs mt-2">
            {t('redirectingSoon', 'Redirection automatique…')}
          </p>
        </motion.div>
      </motion.div>
    </div>
  );
};

export default SuccessPage;
